import { useRef } from 'react';
import { Button, Flex, Spinner, Text } from '@radix-ui/themes';
import { ChevronUp, ChevronRight, File as FileIcon, Folder, Home, Upload } from 'lucide-react';
import { MAX_FILE_BYTES, type FsEntry } from '@mac-remote/protocol';
import { humanSize } from '../lib/fileKind';

interface Props {
  data: { path: string; entries: FsEntry[] } | null;
  pending: boolean;
  disabled: boolean;
  onNavigate: (path: string) => void;
  /** Opens the preview sheet. Whether the bytes are fetched at all is decided there,
   *  from the name and size passed here. */
  onOpen: (path: string, name: string, size: number | null) => void;
  onUpload: (name: string, b64: string) => void;
  /** Said out loud rather than thrown: a file over the ceiling must be refused before
   *  a minute of uploading, not after. */
  onTooBig: (message: string) => void;
}

function parentOf(path: string): string {
  const i = path.lastIndexOf('/');
  return i <= 0 ? '/' : path.slice(0, i);
}

/**
 * A file browser over the Mac's home directory: tap a folder to enter, a file to open it,
 * or upload from the phone into the current folder. The agent confines all
 * of this to the home directory — the path shown is whatever it resolved to.
 */
export function Files({ data, pending, disabled, onNavigate, onOpen, onUpload, onTooBig }: Props) {
  const fileInput = useRef<HTMLInputElement>(null);

  const pickUpload = (file: File) => {
    if (file.size > MAX_FILE_BYTES) {
      onTooBig(
        `«${file.name}» — ${humanSize(file.size)}, а больше ${humanSize(MAX_FILE_BYTES)} за раз не проходит.`,
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const res = reader.result as string;
      // strip the "data:*/*;base64," prefix
      const b64 = res.slice(res.indexOf(',') + 1);
      onUpload(file.name, b64);
    };
    reader.readAsDataURL(file);
  };

  return (
    <Flex direction="column" style={{ height: '100%', minHeight: 0 }}>
      <Flex align="center" gap="2" px="3" py="2" style={{ borderBottom: '1px solid var(--gray-a4)' }}>
        <Button size="1" variant="soft" color="gray" disabled={disabled} onClick={() => onNavigate('~')}>
          <Home size={13} />
        </Button>
        <Button
          size="1"
          variant="soft"
          color="gray"
          disabled={disabled || !data}
          onClick={() => data && onNavigate(parentOf(data.path))}
        >
          <ChevronUp size={13} />
        </Button>
        <Text size="1" style={{ color: 'var(--app-hint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {data?.path ?? '…'}
        </Text>
        {pending && <Spinner size="1" />}
        <Button size="1" variant="soft" color="jade" disabled={disabled || !data} onClick={() => fileInput.current?.click()}>
          <Upload size={13} />
        </Button>
        <input
          ref={fileInput}
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) pickUpload(f);
            e.target.value = '';
          }}
        />
      </Flex>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {!data ? (
          <Flex align="center" justify="center" style={{ height: '100%' }}>
            <Text size="2" style={{ color: 'var(--app-hint)' }}>
              {disabled ? 'Мак не на связи' : 'Загрузка…'}
            </Text>
          </Flex>
        ) : data.entries.length === 0 ? (
          <Flex align="center" justify="center" style={{ height: '100%' }}>
            <Text size="2" style={{ color: 'var(--app-hint)' }}>Пусто</Text>
          </Flex>
        ) : (
          <Flex direction="column" gap="1" p="2">
            {data.entries.map((e) => (
              <button
                key={e.path}
                className="project-row"
                disabled={disabled}
                onClick={() => (e.kind === 'dir' ? onNavigate(e.path) : onOpen(e.path, e.name, e.size))}
              >
                {e.kind === 'dir' ? (
                  <Folder size={16} color="var(--jade-11)" style={{ flex: '0 0 auto' }} />
                ) : (
                  <FileIcon size={16} color="var(--gray-9)" style={{ flex: '0 0 auto' }} />
                )}
                <Text size="2" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>
                  {e.name}
                </Text>
                {e.kind === 'file' && (
                  <>
                    <Text size="1" style={{ color: 'var(--app-hint)', flex: '0 0 auto' }}>{humanSize(e.size)}</Text>
                    {/* A chevron, not a download arrow: the tap opens the file, and
                        what happens next — drawn here or sent to the chat — depends on
                        what it is. Promising a download on iOS was the old lie. */}
                    <ChevronRight size={14} color="var(--gray-9)" style={{ flex: '0 0 auto' }} />
                  </>
                )}
              </button>
            ))}
          </Flex>
        )}
      </div>
    </Flex>
  );
}
